import React, {FC, useState, useEffect} from "react"
import styled, {css} from "styled-components"
import {Row} from "react-bootstrap"

import {Card} from "@irati/ui/src/Layout"
import {H1, H2, H3, H6} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import ga4Tracking from "@irati-web/tracking"
import {Input} from "@irati/ui/src/FormItem"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {useGetQuoteQuery, useUpdateEnquiry} from "@irati/signup/hooks"
import {SignUp, SignUpQuoteOption, SignUpStage} from "@irati/types"
import Validation from "@irati/utils/validation"
import FormattedMessage, {useIntl} from "@irati/utils/lang"

import GenericModal from "@irati/ui/src/GenericModal"
import {UseQueriesResult} from "@irati/utils/services/apollo"
import Link from "@irati/ui/src/Link"
import Loading from "@irati/ui/src/Loading"
import Spinner from "@irati/ui/src/Loading/Spinner"
import SaveAndProgressFooter from "../../components/SignUp/SaveAndProgressFooter"
import SaveAndProgressMobile from "../../components/SignUp/SaveAndProgressMobile"
import TextWithTick from "../../components/SignUp/TextWithTick"
import {MobileFlex} from "../../components/SignUp/styled"
import Brand from "../../components/SignUp/Brand"
import SignUpQuote from "../../components/SignUp/SignUpQuote"
import {hangPromise} from "@irati/utils/promise"

const StyledCard = styled(Card)`
  max-width: 1200px;
  margin: 0 auto;
`

const StyledFlex = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.desktop`
      flex-direction: column;
    `}
  `}
`

const PackageDetailsCard = styled(Card)`
  ${({theme}) => css`
    ${theme.mediaQueries.minWidthMedia.desktop`
      min-width: 500px;
    `}
  `}
`

const StyledRow = styled(Row)`
  ${({theme}) => css`
    max-width: 350px;
    margin-bottom: 8px;
    ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
      max-width: unset;
      input {
        text-align: center;
      }
    `}
  `}
`

const StyledLink = styled(Link)`
  margin-left: 4px;
  text-decoration: underline;
  display: inline-flex;
  padding-bottom: 0;
`

const LOW_LICENCES_ERROR = "platform.selfSignUp.confirmPackage.lowLicencesError"
const HIGH_LICENCES_ERROR = "platform.selfSignUp.confirmPackage.highLicencesError"

interface Props {
  signUp: UseQueriesResult<SignUp>
  onConfirmedPackage: () => void
}

const MINIMUM_LICENCES = 10
const MAXIMUM_LICENCES = 100

const ConfirmPackage: FC<Props> = ({signUp, onConfirmedPackage}) => {
  const {uuid, stage} = signUp?.data || {}
  const [licences, setLicences] = useState(signUp?.data?.enquiry?.licences || 20)
  const [error, setError] = useState<string | boolean>(false)
  const [showBrands, setShowBrands] = useState(false)
  const [isAnnualSelected, setIsAnnualSelected] = useState(signUp?.data?.enquiry?.option?.lengthOfContractInMonths === 1 ? false : true)
  const [showAlert, setShowAlert] = useState(false)
  const [updateEnquiryLoad, setUpdateEnquiryLoad] = useState<Promise<any> | null>(null)
  const {firstName, middleName, lastName, emailAddress, leadId, companyName} = signUp?.data?.enquiry || {}
  const {formatMessage} = useIntl()
  const {load, data} = useGetQuoteQuery(
    {
      uuid
    },
    {
      fetchPolicy: "cache-and-network"
    }
  )

  const validation = Validation.string("platform.defaultValidation.required").matches(
    /\b(10|[1-9][0-9]|50)\b/,
    "platform.selfSignUp.confirmPackage.inputValidation"
  )

  const lengthOfContract = isAnnualSelected ? 12 : 1
  const enquiry = {
    firstName,
    middleName,
    lastName,
    emailAddress,
    leadId,
    licences: Number(licences),
    option: data?.options.find((quote) => quote.lengthOfContractInMonths === lengthOfContract),
    companyName
  }

  const [updateEnquiry, updateEnquiryApollo] = useUpdateEnquiry({variables: {uuid, enquiry}})

  const theme = useTheme()

  useEffect(() => {
    if (updateEnquiryApollo?.data) {
      fetchUntilCompanyStage()
    }
    if (updateEnquiryApollo?.error) {
      setUpdateEnquiryLoad(null)
    }
  }, [updateEnquiryApollo])

  const fetchUntilCompanyStage = () => {
    signUp.refetch().then((res) => {
      if (res?.data?.getSignUp?.stage === SignUpStage.SignUpStageDetailsAndPackage) {
        setTimeout(fetchUntilCompanyStage, 2000)
      } else {
        onConfirmedPackage()
      }
    })
  }

  const confirmPlan = () => {
    ga4Tracking.trackEventWithStore("plan_details_submit", "sbs_params", {
      page: "plan_details_page",
      subscription_type: isAnnualSelected ? "annual" : "monthly",
      step: "2.1",
      licence_count: licences
    })
    updateEnquiry()
    setUpdateEnquiryLoad(hangPromise())
  }

  useEffect(() => {
    ga4Tracking.trackEventWithStore("plan_details_viewed", "sbs_params", {page: "plan_details_page", step: "2", licence_count: licences})
  }, [])

  const handleUpdateLicences = (updatedLicences: number) => {
    if (updatedLicences < MINIMUM_LICENCES) {
      setError(LOW_LICENCES_ERROR)
      setShowAlert(true)
    } else if (updatedLicences > MAXIMUM_LICENCES) {
      setError(HIGH_LICENCES_ERROR)
      setShowAlert(true)
    } else {
      setError(false)
      setShowAlert(false)
    }
    setLicences(updatedLicences)
  }

  const handleRouteToDemoSite = () => {
    window.open(formatMessage({id: "platform.selfSignUp.confirmPackage.demoLink"}))
  }

  const getMinMaxLicences = (): number => {
    if (licences < MINIMUM_LICENCES) {
      return MINIMUM_LICENCES
    } else if (licences > MAXIMUM_LICENCES) {
      return MAXIMUM_LICENCES
    }
    return licences
  }

  return (
    <>
      <StyledCard>
        {showAlert && (
          <Alert
            type={AlertTypes.error}
            title={error}
            link={error === HIGH_LICENCES_ERROR ? "platform.selfSignUp.confirmPackage.bookSalesCall" : undefined}
            onClickLink={handleRouteToDemoSite}
            onHide={() => setShowAlert(false)}
          />
        )}
        <StyledFlex justifyContent="space-between" gap={2}>
          <Flex direction="column" width="100%">
            <H1 color={theme.colors.primary2} marginBottom={2}>
              <FormattedMessage id="platform.selfSignUp.confirmPackage.title" />
            </H1>
            <H3 color={theme.colors.primary2}>
              <FormattedMessage id="platform.selfSignUp.confirmPackage.noEmployees" />
            </H3>
            <StyledRow>
              <Input
                name="numberOfEmployees"
                type="number"
                label="numberOfEmployees"
                hideLabel
                value={licences}
                onChange={handleUpdateLicences}
                validation={validation}
                min={MINIMUM_LICENCES.toString()}
                max={MAXIMUM_LICENCES.toString()}
                disabled={stage === SignUpStage.SignUpStageComplete}
              />
            </StyledRow>
            <H3 color={theme.colors.primary2} marginBottom={2}>
              <FormattedMessage id="platform.selfSignUp.confirmPackage.planTerm" />
            </H3>
            <Loading load={load} Loader={() => <Spinner />}>
              <Flex direction="column" gap={4}>
                {data?.options?.map((quote: SignUpQuoteOption) => {
                  const isAnnualQuote = quote?.lengthOfContractInMonths === 12
                  const isSelected = (isAnnualSelected && isAnnualQuote) || (!isAnnualSelected && !isAnnualQuote)
                  return (
                    <SignUpQuote
                      key={quote?.quoteId}
                      isAnnual={isAnnualQuote}
                      value={isSelected}
                      quote={quote}
                      onClick={() => setIsAnnualSelected(isAnnualQuote)}
                      employees={getMinMaxLicences()}
                      currencyCode={data?.currency}
                    />
                  )
                })}
              </Flex>
            </Loading>
          </Flex>
          <SaveAndProgressMobile
            onConfirm={confirmPlan}
            confirmLoad={updateEnquiryLoad}
            confirmText={formatMessage({id: "platform.selfSignUp.confirmPackage.next"})}
            disabledSave={error || !data?.options}
          />
          <PackageDetailsCard>
            <Flex gap={1} alignItems="center" width="100%">
              <img src={formatMessage({id: "platform.selfSignUp.confirmPackage.img"})} height={75} width={75} alt="confirmPackage" />
              <MobileFlex direction="column" width="100%">
                <H3 color={theme.colors.primary2}>
                  <FormattedMessage id="platform.selfSignUp.confirmPackage.subTitle1" />
                </H3>
                <H6 color={theme.colors.primary2}>
                  <FormattedMessage id="platform.selfSignUp.confirmPackage.subTitle2" />
                  <StyledLink full href={formatMessage({id: "platform.selfSignUp.confirmPackage.link"})} target="_blank">
                    <H6 color={theme.colors.primary2}>
                      <FormattedMessage id="platform.selfSignUp.confirmPackage.subTitle3" />
                    </H6>
                  </StyledLink>
                </H6>
              </MobileFlex>
            </Flex>
            <H2 marginTop={3} marginBottom={1} color={theme.colors.primary2}>
              <FormattedMessage id="platform.selfSignUp.confirmPackage.perkboxPackage" />
            </H2>
            <MobileFlex direction="column" gap={1} width="100%">
              <TextWithTick isTitle text={formatMessage({id: "platform.selfSignUp.confirmPackage.packageText1"})} />
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.confirmPackage.packageText2"})} />
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.confirmPackage.packageText3"})} />
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.confirmPackage.packageText4"})} />
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.confirmPackage.packageText5"})} />
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.confirmPackage.packageText6"})} />
            </MobileFlex>
          </PackageDetailsCard>
        </StyledFlex>
      </StyledCard>
      <SaveAndProgressFooter
        onConfirm={confirmPlan}
        confirmText={formatMessage({id: "platform.selfSignUp.confirmPackage.confirmPlanDetails"})}
        disabledSave={error || !data?.options}
        confirmLoad={updateEnquiryLoad}
      />
      <GenericModal hideActions show={showBrands} onHide={() => setShowBrands(false)}>
        <H3>
          <FormattedMessage id="platform.selfSignUp.confirmPackage.brandsAvailable" />
        </H3>
        <Flex justifyContent="space-between" marginTop={2} marginBottom={4}>
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink1"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands1"})}
          />
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink7"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands7"})}
          />
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink2"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands2"})}
          />
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink3"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands3"})}
          />
        </Flex>
        <Flex justifyContent="space-between" marginBottom={4}>
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink8"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands8"})}
          />
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink9"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands9"})}
          />
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink4"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands4"})}
          />
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink5"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands5"})}
          />
        </Flex>
        <Flex justifyContent="space-between" alignItems="center">
          <Brand
            img={formatMessage({id: "platform.selfSignUp.confirmPackage.brandsLink6"})}
            text={formatMessage({id: "platform.selfSignUp.confirmPackage.brands6"})}
            width="100%"
          />
        </Flex>
      </GenericModal>
    </>
  )
}

export default ConfirmPackage
